import React from 'react';
import { Alert, Box, Button, Spacer, Switch, Table, Text, TextButton } from '@zillow/constellation-10';
import { HR } from '@zg-rentals/zrs-comet';
import {
  CometUserRoleType,
  logError,
  useRentalManagerUser,
  UserPermissionsConstants,
} from '@zg-rentals/rental-manager-env';
import { useDispatch, useSelector } from 'react-redux';
import UserManagementActions from '../../redux/actions/UserManagementActions';
import { EVENT_IDS } from '../../modal/ClickstreamEvents';

interface PermissionSwitchTypes {
  permission: string;
  description: React.ReactNode;
  active: boolean;
  onPermissionChange: (permission: string, active: boolean) => void;
  disabled?: boolean;
}

const PermissionSwitch = ({ permission, description, active, onPermissionChange, disabled }: PermissionSwitchTypes) => (
  <Table.Row
    css={{
      position: 'relative',
      ...(disabled && {
        _after: {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'bg.neutral.secondary',
          opacity: 0.5,
          pointerEvents: 'none',
          zIndex: 1,
          cursor: 'not-allowed',
        },
      }),
    }}
  >
    <Table.Cell>
      <Spacer css={{ paddingBlock: 'md' }}>
        <Box css={{ display: 'flex' }}>
          <Box css={{ flexGrow: 1 }}>
            <Text>{description}</Text>
          </Box>
          <Switch
            disabled={disabled}
            checked={active}
            onClick={() => !disabled && onPermissionChange(permission, !active)}
          />
        </Box>
      </Spacer>
    </Table.Cell>
  </Table.Row>
);

interface PermissionsTypes {
  onCancel: () => void;
  onBack: () => void;
  onNextStep: () => void;
  handleSaveComplete: () => void;
  onPermissionChange?: (permission: string, active: boolean) => void;
}

const otherPermissions = [
  UserPermissionsConstants.PERMISSIONS.ADD_LISTING,
  UserPermissionsConstants.PERMISSIONS.UPDATE_LISTING,
  UserPermissionsConstants.PERMISSIONS.VIEW_INVOICE,
  UserPermissionsConstants.PERMISSIONS.VIEW_CONTACT,
  UserPermissionsConstants.PERMISSIONS.RESPOND_REVIEW,
  UserPermissionsConstants.PERMISSIONS.VIEW_ANALYTICS,
];

const Permissions = ({ onCancel, onBack, onNextStep, handleSaveComplete }: PermissionsTypes) => {
  const dispatch = useDispatch();
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // @ts-ignore
  const currentUser = useSelector((state) => state.userManagement.currentUser);
  const {
    user: { roleTypes = [] },
  } = useRentalManagerUser();
  const { isInvitation } = currentUser;

  const handleUpdatePermission = (permission: string) => {
    const newPermissions = {
      ...currentUser.permissions,
      [permission]: !currentUser.permissions[permission],
    };
    dispatch(UserManagementActions.currentUserSetPermissions(newPermissions));
  };

  React.useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const handleSubmit = () => {
    setIsSubmitting(true);
    setError('');
    dispatch(UserManagementActions.editableUserSave())
      // @ts-ignore
      .then(() => {
        setIsSubmitting(false);
        handleSaveComplete();
        return;
      })
      .catch((error: unknown) => {
        logError({
          error,
          errorType: 'UserManagementActions_editableUserSave',
          context: 'React component: Permissions',
        });
        setIsSubmitting(false);
        setError('An error occurred, please try again later.');
      });
  };

  const isNonAdvertiser = roleTypes.includes(CometUserRoleType.NON_ADVERTISER_CUSTOMER);

  return (
    <div>
      <Spacer css={{ marginBlock: 'sm' }}>
        <Text textStyle="body-bold" asChild>
          {`Set ${currentUser.userName}'s permissions`}
        </Text>
      </Spacer>
      <Table.Root aria-label="Permissions toggle table" size="md">
        <Table.Body>
          {otherPermissions.map((permission) => (
            <PermissionSwitch
              key={permission}
              permission={permission}
              onPermissionChange={() => handleUpdatePermission(permission)}
              description={
                UserPermissionsConstants.DESCRIPTION[permission as keyof typeof UserPermissionsConstants.DESCRIPTION]
                  .DESCRIPTION
              }
              active={currentUser.permissions[permission]}
              disabled={isNonAdvertiser && permission === UserPermissionsConstants.PERMISSIONS.VIEW_INVOICE}
            />
          ))}
        </Table.Body>
      </Table.Root>
      <HR />
      {error && <Alert tone="critical">{error}</Alert>}
      <div className="Questions-footer">
        <TextButton className="Questions-button" onClick={onCancel} data-eventid={EVENT_IDS.CANCEL_ADDING_NEW_USER}>
          Cancel
        </TextButton>
        <div>
          <TextButton className="Questions-button" onClick={onBack} disabled={isSubmitting}>
            Back
          </TextButton>
          {isInvitation ? (
            <Button tone="brand" emphasis="filled" onClick={onNextStep}>
              Continue
            </Button>
          ) : (
            <Button
              tone="brand"
              emphasis="filled"
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Permissions;
